import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  BellOff,
  Smartphone,
  Mail,
  Moon,
  Clock,
  TestTube2,
  Send,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import MenuSheet from "@/components/MenuSheet";
import api from "@/lib/api";
import { requestNotificationPermission, showNotification, notifySupported } from "@/lib/notifications";
import { toast } from "sonner";

const CATEGORY_ROWS = [
  { key: "daily_planner", label: "Daily planner", body: "Morning digest with today's tasks and events." },
  { key: "daily_habits", label: "Daily habits", body: "Reminders for the habits you've committed to." },
  { key: "sunday_reset", label: "Sunday Reset", body: "Weekly check-in nudge every Sunday." },
  { key: "vision_board", label: "Vision Board check-in", body: "Occasional prompts to revisit your board." },
  { key: "future_letters", label: "Letters to Future Self", body: "Delivery notice on the unlock date." },
  { key: "security", label: "Account & security", body: "Password resets, new sign-ins, plan changes." },
  { key: "announcements", label: "Announcements", body: "New features and product updates." },
];

const WEEKDAYS = [
  { i: 0, s: "Mon" },
  { i: 1, s: "Tue" },
  { i: 2, s: "Wed" },
  { i: 3, s: "Thu" },
  { i: 4, s: "Fri" },
  { i: 5, s: "Sat" },
  { i: 6, s: "Sun" },
];

export default function NotificationsPage() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [devicePermission, setDevicePermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/settings");
      setPrefs(data);
    } catch (e) {
      toast.error("Could not load settings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (patch, message = "Saved") => {
    setSaving(true);
    try {
      const { data } = await api.patch("/notifications/settings", patch);
      setPrefs(data);
      if (message) toast.success(message);
    } catch (e) {
      toast.error("Could not save", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (key, value) =>
    save({ categories: { ...prefs.categories, [key]: value } }, null);

  const toggleDay = (day) => {
    const on = prefs.notification_days.includes(day);
    const next = on
      ? prefs.notification_days.filter((d) => d !== day)
      : [...prefs.notification_days, day].sort((a, b) => a - b);
    save({ notification_days: next }, null);
  };

  const grantDevicePermission = async () => {
    if (!notifySupported()) {
      toast("Not supported here", {
        description: "Add Lumivera to your Home Screen to enable device notifications.",
      });
      return;
    }
    const p = await requestNotificationPermission();
    setDevicePermission(p);
    if (p === "granted") {
      toast.success("Notifications enabled");
      if (!prefs.device_enabled) await save({ device_enabled: true }, null);
    } else if (p === "denied") {
      toast.error("Permission blocked", {
        description: "Enable notifications for Lumivera in your browser or phone settings.",
      });
    }
  };

  const testDevice = async () => {
    if (devicePermission !== "granted") {
      await grantDevicePermission();
      if (Notification.permission !== "granted") return;
    }
    showNotification("Lumivera — test", "This is a test notification. Looking good.");
  };

  const testEmail = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/notifications/test-email");
      if (data.ok) {
        toast.success("Test email sent", {
          description: "Check your inbox (and Spam / Promotions).",
        });
      } else {
        toast.error("Could not send test email", {
          description: "Resend rejected the request. Try again shortly.",
        });
      }
    } catch (e) {
      toast.error("Test email failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  const disabled = !prefs.master_enabled;

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-5" data-testid="notifications-page">
        <div className="flex items-center gap-2">
          <MenuSheet />
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="notif-back">
            <Link to="/settings">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to settings
            </Link>
          </Button>
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Notifications
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-0.5">
            Reminders & alerts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose what Lumivera sends you and when. All times use your local timezone.
          </p>
        </div>

        {/* Master toggle */}
        <Card className="rounded-2xl" data-testid="master-card">
          <CardContent className="pt-5 flex items-center gap-3">
            <div className="size-11 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
              {prefs.master_enabled ? (
                <Bell strokeWidth={1.5} className="size-5 text-primary" />
              ) : (
                <BellOff strokeWidth={1.5} className="size-5 text-primary" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-semibold">All notifications</div>
              <div className="text-xs text-muted-foreground">
                Turn every reminder on or off with one switch.
              </div>
            </div>
            <Switch
              checked={prefs.master_enabled}
              onCheckedChange={(v) => save({ master_enabled: v })}
              disabled={saving}
              data-testid="master-toggle"
            />
          </CardContent>
        </Card>

        {/* Channels */}
        <Card className="rounded-2xl" data-testid="channels-card">
          <CardHeader>
            <CardTitle className="text-lg font-display">Channels</CardTitle>
            <CardDescription>Where should Lumivera reach you?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Smartphone strokeWidth={1.5} className="size-5 text-primary shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-sm">Device notifications</div>
                <div className="text-[11px] text-muted-foreground">
                  Native banners on iOS, Android and desktop.{" "}
                  {devicePermission === "granted" ? (
                    <span className="text-primary font-semibold">Permission granted</span>
                  ) : devicePermission === "denied" ? (
                    <span className="text-red-500 font-semibold">Blocked — enable in device settings</span>
                  ) : (
                    <button
                      onClick={grantDevicePermission}
                      className="text-primary font-semibold hover:underline"
                      data-testid="request-permission"
                    >
                      Enable
                    </button>
                  )}
                </div>
              </div>
              <Switch
                checked={prefs.device_enabled && !disabled}
                onCheckedChange={(v) => save({ device_enabled: v }, null)}
                disabled={disabled || saving}
                data-testid="device-toggle"
              />
            </div>

            <div className="flex items-center gap-3">
              <Mail strokeWidth={1.5} className="size-5 text-primary shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-sm">Email notifications</div>
                <div className="text-[11px] text-muted-foreground">
                  Sent via Resend from noreply@lumiveralife.com.
                </div>
              </div>
              <Switch
                checked={prefs.email_enabled && !disabled}
                onCheckedChange={(v) => save({ email_enabled: v }, null)}
                disabled={disabled || saving}
                data-testid="email-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <Card className="rounded-2xl" data-testid="categories-card">
          <CardHeader>
            <CardTitle className="text-lg font-display">What to send</CardTitle>
            <CardDescription>Fine-tune the reminders you receive.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {CATEGORY_ROWS.map((row) => (
                <li
                  key={row.key}
                  className="py-3 flex items-start gap-3"
                  data-testid={`category-${row.key}`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{row.label}</div>
                    <div className="text-[11px] text-muted-foreground">{row.body}</div>
                  </div>
                  <Switch
                    checked={Boolean(prefs.categories?.[row.key]) && !disabled}
                    onCheckedChange={(v) => toggleCategory(row.key, v)}
                    disabled={disabled || saving}
                    data-testid={`toggle-${row.key}`}
                  />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Time & days */}
        <Card className="rounded-2xl" data-testid="schedule-card">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Clock strokeWidth={1.5} className="size-5 text-primary" />
              When
            </CardTitle>
            <CardDescription>Daily reminders send at this time on the days you pick.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Notification time
                </label>
                <Input
                  type="time"
                  value={prefs.notification_time}
                  onChange={(e) => setPrefs({ ...prefs, notification_time: e.target.value })}
                  onBlur={() => save({ notification_time: prefs.notification_time }, null)}
                  disabled={disabled}
                  className="rounded-xl"
                  data-testid="time-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Timezone
                </label>
                <Input
                  value={prefs.timezone}
                  onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
                  onBlur={() => save({ timezone: prefs.timezone }, null)}
                  placeholder={browserTz}
                  disabled={disabled}
                  className="rounded-xl font-mono text-sm"
                  data-testid="tz-input"
                />
                <p className="text-[10px] text-muted-foreground">e.g. {browserTz}</p>
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Days
              </label>
              <div className="mt-1.5 flex gap-1.5 flex-wrap" data-testid="days-picker">
                {WEEKDAYS.map((d) => {
                  const on = prefs.notification_days.includes(d.i);
                  return (
                    <button
                      key={d.i}
                      onClick={() => toggleDay(d.i)}
                      disabled={disabled || saving}
                      className={`h-9 min-w-[46px] px-3 rounded-full text-xs font-semibold border transition ${
                        on
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-transparent border-border text-muted-foreground hover:border-primary/50"
                      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      data-testid={`day-${d.i}`}
                    >
                      {d.s}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quiet hours */}
        <Card className="rounded-2xl" data-testid="quiet-card">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Moon strokeWidth={1.5} className="size-5 text-primary" />
              Quiet hours
            </CardTitle>
            <CardDescription>Suppress everything except security alerts overnight.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Starts
              </label>
              <Input
                type="time"
                value={prefs.quiet_hours_start}
                onChange={(e) => setPrefs({ ...prefs, quiet_hours_start: e.target.value })}
                onBlur={() => save({ quiet_hours_start: prefs.quiet_hours_start }, null)}
                disabled={disabled}
                className="rounded-xl"
                data-testid="quiet-start-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Ends
              </label>
              <Input
                type="time"
                value={prefs.quiet_hours_end}
                onChange={(e) => setPrefs({ ...prefs, quiet_hours_end: e.target.value })}
                onBlur={() => save({ quiet_hours_end: prefs.quiet_hours_end }, null)}
                disabled={disabled}
                className="rounded-xl"
                data-testid="quiet-end-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Test buttons */}
        <Card className="rounded-2xl" data-testid="test-card">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <TestTube2 strokeWidth={1.5} className="size-5 text-primary" />
              Test your setup
            </CardTitle>
            <CardDescription>Send a sample so you can see exactly what Lumivera sends.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={testDevice}
              disabled={disabled}
              className="rounded-xl"
              data-testid="test-device-button"
            >
              <Smartphone strokeWidth={1.5} className="size-4 mr-1.5" />
              Test device notification
            </Button>
            <Button
              onClick={testEmail}
              disabled={disabled || saving}
              className="rounded-xl"
              data-testid="test-email-button"
            >
              <Send strokeWidth={1.5} className="size-4 mr-1.5" />
              Test email
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
